import { Box } from "@mui/material";
import { useContext, useState } from "react";

import { UploadDocumentZone } from "../../../../components";
import RefreshLoading from "../../../../components/refreshLoading";
import {
  ACCEPTED_FILE_FORMATS,
  ACCEPTED_FILE_FORMATS_LABEL,
  MAX_MB_RFP_FILE_SIZE,
} from "../../../../constants";
import { RfpContext } from "../../contexts";
import { generateUniqueFileName } from "../../utils";

import UploadedDocs from "./modals/uploadedDocs";

const DocumentUpload = ({
  initialExternals,
  uploadedFiles,
  files,
  documentTypeName,
  setFiles,
  externalFiles,
  setExternalFiles,
}) => {
  const { uploadRFPDocument, isDocumentUploading, updateRFPDocument } =
    useContext(RfpContext);
  const [openUploadedDocs, setOpenUploadedDocs] = useState(false);

  const onDelete = (file) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.name !== file.name));
  };

  const processIncomingFiles = async (_files) => {
    const allFileNames = new Set(
      [...files, ...uploadedFiles.map((item) => item.file)].map(
        (item) => item.name,
      ),
    );
    const newFiles = _files.reduce((acc, item) => {
      const uniqueName = generateUniqueFileName(item.name, allFileNames);
      allFileNames.add(uniqueName);
      if (uniqueName !== item.name) {
        const newFile = new File([item], uniqueName, { type: item.type });
        newFile.path = uniqueName;
        return [...acc, newFile];
      }
      return [...acc, item];
    }, []);
    const result = await uploadRFPDocument(newFiles, documentTypeName);
    if (result?.status === 200) {
      const renamedFiles = newFiles.map((originalFile, i) => {
        const renamedName = result.data?.[i]?.filename ?? originalFile.name;
        return renamedName === originalFile.name
          ? originalFile
          : new File([originalFile], renamedName, { type: originalFile.type });
      });
      setFiles((prevFiles) => [...prevFiles, ...renamedFiles]);
    }
  };

  const onCancel = () => {
    setOpenUploadedDocs(false);
  };

  const onSaveDocument = async (saveDocuments) => {
    const filesNewlyAssigned = uploadedFiles.filter((itemUploaded) =>
      saveDocuments.some((newDoc) => newDoc.name === itemUploaded.file.name),
    );
    const filesToBeUpdated = [];
    if (filesNewlyAssigned.length) {
      filesNewlyAssigned.forEach((item) => {
        const localDocumentTypes = Object.keys(item.documentTypes || {});
        if (!localDocumentTypes.includes(documentTypeName)) {
          filesToBeUpdated.push({
            id: item.file.id,
            documentType: [...localDocumentTypes, documentTypeName],
          });
        }
      });
    }

    if (filesToBeUpdated.length) {
      await updateRFPDocument(filesToBeUpdated, true);
    }
    setOpenUploadedDocs(false);
  };

  return (
    <Box>
      <UploadDocumentZone
        files={files}
        handleUploadFiles={(_files) => processIncomingFiles(_files)}
        maxSize={MAX_MB_RFP_FILE_SIZE}
        acceptedFormatFiles={ACCEPTED_FILE_FORMATS}
        allowedFormatLabel={ACCEPTED_FILE_FORMATS_LABEL}
        isMultiple={true}
        documentTypeName={documentTypeName}
        isAllowToManage={true}
        onFileDelete={onDelete}
        uploadedFiles={uploadedFiles}
        setOpenUploadedDocs={setOpenUploadedDocs}
      />
      {openUploadedDocs && (
        <UploadedDocs
          isOpen={openUploadedDocs}
          onCancel={onCancel}
          onSaveDocuments={onSaveDocument}
          externalFiles={externalFiles}
          uploadedFiles={uploadedFiles}
          documentTypeName={documentTypeName}
          initialExternals={initialExternals}
          setExternalFiles={setExternalFiles}
        />
      )}
      {isDocumentUploading && <RefreshLoading />}
      <Box display="flex" justifyContent="flex-end" mt={2}></Box>
    </Box>
  );
};

export default DocumentUpload;
